import AddressField from "@/ui/composed/form/AddressField";
import ReadOnlyField from "@/ui/composed/form/ReadOnlyField";
import type { FormFieldProps } from "@/ui/composed/form/types";
import { FormFieldType } from "@/ui/composed/form/types";
import DateTimePicker from "@/ui/composed/pickers/DateTimePicker";
import MultiSelectTextField from "@/ui/composed/pickers/MultiSelectTextField";
import CheckBoxField from "@/ui/core/CheckBoxField";
import LabeledInput from "@/ui/core/LabeledInput";
import TextField from "@/ui/core/TextField";
import { joinTestIDs } from "@/utils/string.util";

// eslint-disable-next-line no-restricted-syntax
const FormField = ({
  hasError,
  isDisabled,
  isHidden = false,
  isReadOnly = false,
  isRequired,
  isSearchable,
  leftText,
  name,
  onChange,
  options,
  placeholder = "",
  rightText,
  subLabel,
  testID,
  type,
  value,
}: FormFieldProps) => {
  const baseTestID = joinTestIDs(testID, name);
  const getFormFieldComponent = () => {
    if (isReadOnly) {
      return (
        <LabeledInput leftText={leftText} testID={baseTestID}>
          <ReadOnlyField testID={baseTestID} type={type} value={value} />
        </LabeledInput>
      );
    }

    switch (type) {
      case FormFieldType.Address: {
        return (
          <AddressField
            hasError={hasError}
            isDisabled={isDisabled}
            onChange={onChange}
            testID={baseTestID}
            value={value}
          />
        );
      }
      case FormFieldType.CheckBox: {
        return (
          <CheckBoxField
            isChecked={value}
            isDisabled={isDisabled}
            leftText={leftText}
            onChange={onChange}
            rightText={rightText}
            subLabel={subLabel}
            testID={baseTestID}
          />
        );
      }
      case FormFieldType.Date: {
        return (
          <DateTimePicker
            date={value}
            isDisabled={isDisabled}
            mode="date"
            onChange={onChange}
            testID={baseTestID}
          />
        );
      }
      case FormFieldType.Select: {
        return (
          <MultiSelectTextField
            hasError={hasError}
            isDisabled={isDisabled}
            isSearchable={isSearchable}
            isSingleSelect
            modalTitle={leftText ?? "Select option"}
            onValuesChange={(selectedOptions) => {
              onChange(selectedOptions[0]?.value);
            }}
            options={options}
            testID={baseTestID}
            value={value}
          />
        );
      }
      case FormFieldType.Text: {
        return (
          <TextField
            hasError={hasError}
            isDisabled={isDisabled}
            onChange={onChange}
            placeholder={placeholder}
            testID={baseTestID}
            value={value}
          />
        );
      }
      default:
        return null;
    }
  };

  if (isReadOnly) {
    return (
      <LabeledInput leftText={leftText} testID={baseTestID}>
        <ReadOnlyField testID={baseTestID} type={type} value={value} />
      </LabeledInput>
    );
  }

  // single checkboxes are self-labeled and do not require LabeledInput
  if ([FormFieldType.CheckBox].includes(type)) {
    return getFormFieldComponent();
  }

  return isHidden ? null : (
    <LabeledInput
      hasError={hasError}
      isOptional={!isRequired}
      isRequired={isRequired && !isReadOnly}
      leftText={leftText}
      rightText={rightText}
      subLabel={subLabel}
      testID={baseTestID}
    >
      {getFormFieldComponent()}
    </LabeledInput>
  );
};

export default FormField;
